import { Bell, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { ComingSoonWatermark } from '../components/ComingSoonWatermark';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

/**
 * NotificationsPage - User notification center
 * 
 * This page displays:
 * - User-specific notifications and alerts
 * - System announcements
 * - Task assignments and reminders
 * - Workflow approvals and updates
 * - Notification preferences
 */
export function NotificationsPage() {
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-6 relative">
      <ComingSoonWatermark
        pageKey="notifications-center"
        icon={Bell}
        title="Notifications Center Coming Soon"
        description="View and manage your notifications, alerts, and system updates."
      />
      
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Notifications</h1>
          <p className="text-neutral-400 mt-2">
            View and manage your notifications, alerts, and system updates
          </p>
        </div>
        <button className="btn-outline">
          <CheckCircle className="w-4 h-4 mr-2" />
          Mark All Read
        </button>
      </div>

      {/* Placeholder Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Bell className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Total</p>
              <p className="text-2xl font-bold text-white">23</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-danger-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-danger-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Urgent</p>
              <p className="text-2xl font-bold text-white">5</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-warning-500/20 rounded-lg">
              <Info className="w-5 h-5 text-warning-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Unread</p>
              <p className="text-2xl font-bold text-white">12</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-success-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-success-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Read</p>
              <p className="text-2xl font-bold text-white">11</p>
            </div>
          </div>
        </div>
      </div>

      {/* Coming Soon Content */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">Notification Center</h2>
        </div>
        <div className="p-6">
          <p className="text-neutral-400 mb-4">
            This page will provide a comprehensive notification management system:
          </p>
          <ul className="space-y-2 text-neutral-400">
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-1">•</span>
              <span>Unified inbox for all notifications (alerts, tasks, approvals, announcements)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-1">•</span>
              <span>Filtering by type, priority, read/unread status, and date range</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-1">•</span>
              <span>Quick actions from notification list (approve, dismiss, snooze)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-1">•</span>
              <span>Notification preferences and delivery channel settings (email, in-app, SMS)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-1">•</span>
              <span>Digest scheduling and batch notification options</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-1">•</span>
              <span>Do Not Disturb mode and quiet hours configuration</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-1">•</span>
              <span>Notification history and archiving</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
